import { AppSelect } from '../../ui/AppSelect';
import React, { useState, useMemo } from 'react';
import { X, Play, Clock, Film, Settings, Pencil, Check } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import { VIDEO_MODELS } from './Constants';
import {
  WANX_VIDEO_MODEL,
  WANX_VIDEO_MODELS,
  WANX_VIDEO_RESOLUTIONS,
  normalizeWanxVideoModel,
} from '../../../utils/wanxVideoCapabilities';

const VIDU_SUB_MODELS = VIDEO_MODELS.find(m => m.id === 'vidu')?.subModels || [];
const WANX_SUB_MODELS = WANX_VIDEO_MODELS.map(({ value, label }) => ({
  id: value,
  name: label,
  durationRange: [2, 30],
  defaultDuration: 5,
  resolutions: [...WANX_VIDEO_RESOLUTIONS],
  defaultResolution: '1080P',
}));

const AUDIO_TYPE_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'speech_only', label: '仅语音' },
  { value: 'sound_effect_only', label: '仅音效' },
];

const MOVEMENT_OPTIONS = [
  { value: 'auto', label: '自动' },
  { value: 'small', label: '小' },
  { value: 'medium', label: '中' },
  { value: 'large', label: '大' },
];

const CREDITS_PER_SEC = {
  'viduq3-pro': { '1080p': 32, '720p': 30, '540p': 14 },
  'viduq3-turbo': { '1080p': 16, '720p': 12, '540p': 8 },
};

const Toggle = ({ value, onChange, disabled }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      className="relative w-8 h-4 rounded-full transition-colors shrink-0"
      style={{
        backgroundColor: value && !disabled ? theme.colors.bluePrimary : 'var(--color-bg-tertiary)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="absolute top-0.5 w-3 h-3 rounded-full transition-transform"
        style={{
          backgroundColor: 'white',
          left: value && !disabled ? '17px' : '2px',
        }}
      />
    </button>
  );
};

const BatchVideoPanel = ({ shots, onGenerate, onClose, isGenerating, onShotPromptChange, exiting }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  // 提示词编辑状态：{ shotId, text } 或 null
  const [editingPrompt, setEditingPrompt] = useState(null);

  // 过滤出有图片的分镜
  const validShots = useMemo(() => shots.filter(s => s.generated_image), [shots]);

  // Provider & Model
  const [provider, setProvider] = useState('vidu');
  const [model, setModel] = useState('viduq3-pro');

  // 每个分镜的时长设置
  const [shotDurations, setShotDurations] = useState(() => {
    const durations = {};
    validShots.forEach(shot => {
      const suggestedTime = shot.time || '5s';
      const timeValue = parseInt(suggestedTime.replace('s', ''), 10) || 5;
      durations[shot.id] = Math.min(Math.max(timeValue, 1), 16);
    });
    return durations;
  });

  // Vidu 参数
  const [resolution, setResolution] = useState('720p');
  const [audio, setAudio] = useState(true);
  const [audioType, setAudioType] = useState('all');
  const [bgm, setBgm] = useState(false);
  const [offPeak, setOffPeak] = useState(false);
  const [movementAmplitude, setMovementAmplitude] = useState('auto');
  const [voiceId, setVoiceId] = useState('');
  const [seed, setSeed] = useState('');

  const activeSubModels = provider === 'wanx' ? WANX_SUB_MODELS : VIDU_SUB_MODELS;
  const currentSubModel = activeSubModels.find(m => m.id === model) || activeSubModels[0];

  // 积分消耗表（每秒）
  const totalCredits = useMemo(() => {
    const table = CREDITS_PER_SEC[model];
    if (!table) return null;
    const perSec = table[resolution];
    if (!perSec) return null;
    const totalSecs = Object.values(shotDurations).reduce((sum, d) => sum + d, 0);
    const credits = totalSecs * perSec * (offPeak ? 0.5 : 1);
    return credits;
  }, [model, resolution, offPeak, shotDurations]);

  // 根据子模型获取可用分辨率
  const availableResolutions = useMemo(() => {
    if (!currentSubModel) return ['720p'];
    return currentSubModel.resolutions || ['720p'];
  }, [currentSubModel]);

  // BGM 是否可用
  const bgmDisabled = useMemo(() => {
    if (!currentSubModel) return true;
    if (!currentSubModel.supportsBgm) return true;
    // q2 在 9s/10s 时不支持 BGM
    if (model.startsWith('viduq2')) {
      const maxDuration = Math.max(...Object.values(shotDurations));
      if (maxDuration >= 9) return true;
    }
    return false;
  }, [currentSubModel, model, shotDurations]);

  const handleDurationChange = (shotId, duration) => {
    setShotDurations(prev => ({
      ...prev,
      [shotId]: parseInt(duration, 10)
    }));
  };

  const handleModelChange = (newModel) => {
    setModel(newModel);

    const sm = activeSubModels.find(m => m.id === newModel);
    if (!sm) return;

    // 重置分辨率
    if (!sm.resolutions.includes(resolution)) {
      setResolution(sm.defaultResolution);
    }

    // 重置时长到模型范围内
    const [min, max] = sm.durationRange;
    setShotDurations(prev => {
      const updated = {};
      Object.keys(prev).forEach(shotId => {
        const cur = prev[shotId];
        updated[shotId] = Math.max(min, Math.min(max, cur));
      });
      return updated;
    });
  };

  const handleProviderChange = (nextProvider) => {
    setProvider(nextProvider);
    if (nextProvider === 'wanx') {
      setModel(WANX_VIDEO_MODEL);
      setResolution('1080P');
      setShotDurations((previous) => Object.fromEntries(
        Object.entries(previous).map(([shotId, value]) => [shotId, Math.min(30, Math.max(2, Number(value) || 5))]),
      ));
      return;
    }
    if (nextProvider === 'vidu') {
      const fallback = VIDU_SUB_MODELS.find((item) => item.id === 'viduq3-pro') || VIDU_SUB_MODELS[0];
      setModel(fallback?.id || 'viduq3-pro');
      setResolution(fallback?.defaultResolution || '720p');
    }
  };

  const handleGenerate = () => {
    const config = {
      provider,
      model,
      shotDurations,
    };

    if (provider === 'vidu') {
      config.resolution = resolution;
      config.audio = audio;
      config.audioType = currentSubModel?.supportsAudioType ? audioType : undefined;
      config.bgm = currentSubModel?.supportsBgm && !bgmDisabled ? bgm : undefined;
      config.offPeak = offPeak;
      config.movementAmplitude = currentSubModel?.supportsMovementAmplitude ? movementAmplitude : undefined;
      config.voiceId = currentSubModel?.supportsVoiceId && voiceId ? voiceId : undefined;
      config.seed = seed ? parseInt(seed, 10) : undefined;
    } else if (provider === 'wanx') {
      config.model = normalizeWanxVideoModel(model);
      config.resolution = resolution;
      config.audio = audio;
      config.promptExtend = true;
    }

    onGenerate(config);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--overlay-medium)] backdrop-blur-sm ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}>
      <div
        className={`w-full max-w-6xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: theme.colors.purplePrimary }}
            >
              <Film size={20} style={{ color: "var(--color-accent-contrast)" }} />
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
                一键生成视频
              </h3>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                为所有分镜批量生成视频
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="grid grid-cols-[1fr_300px] flex-1 min-h-0">
            {/* Left: Shot List */}
            <div className="p-6 border-r overflow-y-auto custom-scrollbar" style={{ borderColor: 'var(--color-border-primary)' }}>
              {validShots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-bg-overlay)' }}
                  >
                    <Film size={32} style={{ color: 'var(--color-text-muted)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    没有可用的图片，请先生成分镜图片
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {validShots.map((shot, index) => (
                    <div
                      key={shot.id}
                      className="flex items-center gap-3 p-3 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-overlay)',
                        border: '1px solid var(--color-border-primary)'
                      }}
                    >
                      {/* Thumbnail */}
                      <div className="w-20 h-12 rounded overflow-hidden shrink-0" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                        <img
                          src={shot.generated_image}
                          alt={`分镜 ${shot.shot_number || index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded"
                            style={{
                              backgroundColor: theme.colors.purplePrimary,
                              color: 'white'
                            }}
                          >
                            #{shot.shot_number || index + 1}
                          </span>
                        </div>
                        {editingPrompt?.shotId === shot.id ? (
                          <div className="flex gap-1">
                            <textarea
                              autoFocus
                              value={editingPrompt.text}
                              onChange={(e) => setEditingPrompt(prev => ({ ...prev, text: e.target.value }))}
                              rows={3}
                              className="flex-1 text-xs rounded px-2 py-1 outline-none resize-none"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                border: `1px solid ${theme.colors.accentPrimary}`,
                                color: 'var(--color-text-secondary)',
                              }}
                            />
                            <button
                              onClick={() => {
                                onShotPromptChange?.(shot.id, editingPrompt.text);
                                setEditingPrompt(null);
                              }}
                              className="p-1.5 rounded-lg shrink-0 self-end"
                              style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                              title="确认"
                            >
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1 group/prompt">
                            <span className="text-xs line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                              {shot.video_prompt || shot.videoPrompt || shot.visual_content || shot.visualContent || '无描述'}
                            </span>
                            <button
                              onClick={() => setEditingPrompt({
                                shotId: shot.id,
                                text: shot.video_prompt || shot.videoPrompt || shot.visual_content || shot.visualContent || ''
                              })}
                              className="p-1 rounded opacity-0 group-hover/prompt:opacity-100 transition-opacity shrink-0"
                              style={{ color: 'var(--color-text-muted)' }}
                              title="编辑提示词"
                            >
                              <Pencil size={12} />
                            </button>
                          </div>
                        )}
                        <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                          建议时长: {shot.time || '5s'}
                        </div>
                      </div>

                      {/* Duration Selector */}
                      <AppSelect
                        value={shotDurations[shot.id] || 5}
                        onChange={(e) => handleDurationChange(shot.id, e.target.value)}
                        className="px-2 py-1 rounded text-xs outline-none"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          border: '1px solid var(--color-border-primary)',
                          color: 'var(--color-text-primary)'
                        }}
                        disabled={isGenerating}
                      >
                        {(() => {
                          const sm = currentSubModel;
                          if (!sm) return null;
                          const [dMin, dMax] = sm.durationRange;
                          return [...Array(dMax - dMin + 1)].map((_, i) => {
                            const dur = dMin + i;
                            return (
                              <option key={dur} value={dur}>{dur}s</option>
                            );
                          });
                        })()}
                      </AppSelect>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Settings */}
            <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <div className="flex items-center gap-2 mb-4">
                <Settings size={16} style={{ color: theme.colors.bluePrimary }} />
                <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  视频参数
                </span>
              </div>

              {/* Provider */}
              <div>
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                  提供商
                </label>
                <AppSelect
                  value={provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--color-bg-overlay)',
                    border: '1px solid var(--color-border-primary)',
                    color: 'var(--color-text-primary)'
                  }}
                  disabled={isGenerating}
                >
                  {VIDEO_MODELS.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </AppSelect>
              </div>

              {/* Vidu Sub-Model */}
              {provider === 'vidu' && (
                <>
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                      子模型
                    </label>
                    <AppSelect
                      value={model}
                      onChange={(e) => handleModelChange(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{
                        backgroundColor: 'var(--color-bg-overlay)',
                        border: '1px solid var(--color-border-primary)',
                        color: 'var(--color-text-primary)'
                      }}
                      disabled={isGenerating}
                    >
                      {VIDU_SUB_MODELS.map(sm => (
                        <option key={sm.id} value={sm.id}>{sm.name}</option>
                      ))}
                    </AppSelect>
                  </div>

                  {/* Resolution */}
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                      分辨率
                    </label>
                    <div className="flex gap-2">
                      {availableResolutions.map(r => (
                        <button
                          key={r}
                          onClick={() => setResolution(r)}
                          className="flex-1 py-2 text-xs rounded-md transition-all font-medium"
                          style={{
                            backgroundColor: resolution === r ? theme.colors.bluePrimary : 'var(--color-bg-overlay)',
                            color: resolution === r ? 'white' : 'var(--color-text-secondary)',
                            border: `1px solid ${resolution === r ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                          }}
                          disabled={isGenerating}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Audio */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                        音频
                      </label>
                      <Toggle value={audio} onChange={setAudio} disabled={isGenerating} />
                    </div>

                    {/* Audio Type */}
                    {currentSubModel?.supportsAudioType && (
                      <div>
                        <label className="text-2xs mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                          音频类型
                        </label>
                        <div className="flex gap-1">
                          {AUDIO_TYPE_OPTIONS.map(opt => {
                            const isSel = audioType === opt.value;
                            return (
                              <button
                                key={opt.value}
                                onClick={() => setAudioType(opt.value)}
                                className="flex-1 py-1 text-2xs rounded transition-all"
                                style={{
                                  backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-overlay)',
                                  color: isSel ? 'white' : 'var(--color-text-muted)',
                                  border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                                }}
                                disabled={isGenerating}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BGM */}
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: bgmDisabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}>
                      BGM {bgmDisabled && <span className="text-2xs opacity-60">(不支持)</span>}
                    </label>
                    <Toggle value={!bgmDisabled && bgm} onChange={setBgm} disabled={isGenerating || bgmDisabled} />
                  </div>

                  {/* Off-Peak */}
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      错峰生成
                    </label>
                    <Toggle value={offPeak} onChange={setOffPeak} disabled={isGenerating} />
                  </div>

                  {/* Movement Amplitude */}
                  {currentSubModel?.supportsMovementAmplitude && (
                    <div>
                      <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                        运动幅度
                      </label>
                      <div className="flex gap-1">
                        {MOVEMENT_OPTIONS.map(opt => {
                          const isSel = movementAmplitude === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setMovementAmplitude(opt.value)}
                              className="flex-1 py-1.5 text-2xs rounded-md transition-all font-medium"
                              style={{
                                backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-overlay)',
                                color: isSel ? 'white' : 'var(--color-text-secondary)',
                                border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                              }}
                              disabled={isGenerating}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Voice ID */}
                  {currentSubModel?.supportsVoiceId && (
                    <div>
                      <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                        Voice ID
                      </label>
                      <input
                        type="text"
                        placeholder="可选，留空使用默认"
                        value={voiceId}
                        onChange={(e) => setVoiceId(e.target.value)}
                        className="w-full text-xs px-2 py-2 rounded"
                        style={{
                          backgroundColor: 'var(--color-bg-overlay)',
                          border: '1px solid var(--color-border-primary)',
                          color: 'var(--color-text-primary)',
                          outline: 'none',
                        }}
                        disabled={isGenerating}
                      />
                    </div>
                  )}

                  {/* Seed */}
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                      随机种子
                    </label>
                    <input
                      type="number"
                      placeholder="可选，留空随机"
                      value={seed}
                      onChange={(e) => setSeed(e.target.value)}
                      className="w-full text-xs px-2 py-2 rounded"
                      style={{
                        backgroundColor: 'var(--color-bg-overlay)',
                        border: '1px solid var(--color-border-primary)',
                        color: 'var(--color-text-primary)',
                        outline: 'none',
                      }}
                      disabled={isGenerating}
                    />
                  </div>
                </>
              )}

              {provider === 'wanx' && (
                <>
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                      模型版本
                    </label>
                    <AppSelect
                      value={normalizeWanxVideoModel(model)}
                      onChange={(event) => handleModelChange(event.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{
                        backgroundColor: 'var(--color-bg-overlay)',
                        border: '1px solid var(--color-border-primary)',
                        color: 'var(--color-text-primary)',
                      }}
                      disabled={isGenerating}
                    >
                      {WANX_SUB_MODELS.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </AppSelect>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                      分辨率
                    </label>
                    <div className="flex gap-2">
                      {WANX_VIDEO_RESOLUTIONS.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setResolution(item)}
                          className="flex-1 py-2 text-xs rounded-md transition-all font-medium"
                          style={{
                            backgroundColor: resolution === item ? theme.colors.bluePrimary : 'var(--color-bg-overlay)',
                            color: resolution === item ? 'white' : 'var(--color-text-secondary)',
                            border: `1px solid ${resolution === item ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                          }}
                          disabled={isGenerating}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      生成音频
                    </label>
                    <Toggle value={audio} onChange={setAudio} disabled={isGenerating} />
                  </div>
                  <p className="text-2xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                    每个分镜使用当前生成图作为首帧；任务成功后系统会立即转存视频，服务商结果链接仅保留 24 小时。
                  </p>
                </>
              )}

            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex items-center justify-between"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-primary)'
          }}
        >
          <div className="text-xs space-y-0.5" style={{ color: 'var(--color-text-muted)' }}>
            <div>共 {validShots.length} 个分镜 · 预计耗时 {validShots.length * 90}-{validShots.length * 120}秒</div>
            <div className="flex items-center gap-3">
              <span>
                总时长：
                <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  {Object.values(shotDurations).reduce((sum, d) => sum + d, 0)}s
                </span>
              </span>
              {totalCredits !== null && (
                <span>
                  预计消耗：
                  <span className="font-bold" style={{ color: offPeak ? '#f59e0b' : 'var(--color-text-primary)' }}>
                    {totalCredits} 积分
                  </span>
                  {offPeak && <span className="ml-1 opacity-60">(错峰)</span>}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleGenerate}
            disabled={validShots.length === 0 || isGenerating}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: theme.colors.purplePrimary,
              color: 'white'
            }}
          >
            {isGenerating ? (
              <>
                <Clock size={16} className="animate-spin" />
                <span>生成中...</span>
              </>
            ) : (
              <>
                <Play size={16} fill="currentColor" />
                <span>开始生成</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchVideoPanel;
